import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { ROUTE_PATHS } from '../config/navigation';
import {
  getCorporateContentFallback,
  hydrateCorporateContent
} from '../data/corporateContent';
import { getSelfServiceContentFallback, hydrateSelfServiceContent } from '../data/selfServiceContent';
import { ORGANIZATION_BRAND } from '../config/organization';

const fallbackAnnouncements = [
  {
    title: 'İş Sağlığı ve Güvenliği duyurusu',
    content: 'Güncellenen İSG prosedürleri dokuman merkezinde yayımlandı.',
    createdAt: new Date().toISOString(),
    author: { firstName: 'Koloğlu', lastName: 'Holding' }
  },
  {
    title: 'Yıl sonu etkinlik takvimi paylaşımı',
    content: 'Yıl sonu organizasyon planı ve katılım detayları etkinlik takviminde yayımlandı.',
    createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    author: { firstName: 'Kurumsal', lastName: 'İletişim' }
  }
];

const getCurrentLunchMenu = (menuItems) => {
  if (!menuItems.length) {
    return null;
  }

  const weekdayIndex = new Date().getDay();
  const weekdayMap = {
    1: 'Pazartesi',
    2: 'Salı',
    3: 'Çarşamba',
    4: 'Perşembe',
    5: 'Cuma'
  };

  const todayMenu = menuItems.find((item) => item.day === weekdayMap[weekdayIndex]);
  return todayMenu || menuItems[0];
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [corporateContent, setCorporateContent] = useState(() => getCorporateContentFallback());
  const [selfServiceContent, setSelfServiceContent] = useState(() => getSelfServiceContentFallback());
  const [dashboardData, setDashboardData] = useState({
    announcements: [],
    statistics: {
      totalUsers: 0,
      totalAnnouncements: 0,
      totalDocuments: 0,
      totalProjects: 0
    },
    birthdays: []
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        const [countData, managedContent] = await Promise.all([
          api.getCounts(),
          api.getManagedContent()
        ]);

        const statistics = countData.data?.statistics || {
          totalUsers: 0,
          totalAnnouncements: 0,
          totalDocuments: 0,
          totalProjects: 0
        };

        setCorporateContent(
          managedContent?.success
            ? hydrateCorporateContent(managedContent.data?.corporate || {})
            : getCorporateContentFallback()
        );
        setSelfServiceContent(
          managedContent?.success
            ? hydrateSelfServiceContent(managedContent.data?.selfService || {})
            : getSelfServiceContentFallback()
        );

        setDashboardData({
          announcements:
            countData.data?.latestAnnouncements?.length > 0
              ? countData.data.latestAnnouncements
              : fallbackAnnouncements,
          statistics,
          birthdays: countData.data?.recentBirthdays || []
        });
      } catch (error) {
        console.error('Dashboard verileri yüklenirken hata:', error);
        setCorporateContent(getCorporateContentFallback());
        setSelfServiceContent(getSelfServiceContentFallback());
        setDashboardData({
          announcements: fallbackAnnouncements,
          statistics: {
            totalUsers: 0,
            totalAnnouncements: fallbackAnnouncements.length,
            totalDocuments: 0,
            totalProjects: 0
          },
          birthdays: []
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const openLink = (path, external = false) => {
    if (external) {
      window.open(path, '_blank', 'noopener,noreferrer');
      return;
    }

    navigate(path);
  };

  const managementMessages = corporateContent.managementMessages || [];
  const cultureHighlights = corporateContent.cultureHighlights || [];
  const participationLinks = corporateContent.participationLinks || [];
  const corporateQuickAccess = corporateContent.corporateQuickAccess || [];
  const quickContacts = corporateContent.quickContacts || [];
  const systemLinks = corporateContent.systemLinks || [];
  const operationsContacts = selfServiceContent.operationsContacts || [];
  const techFaqs = selfServiceContent.techFaqs || [];
  const currentLunchMenu = getCurrentLunchMenu(selfServiceContent.lunchMenu || []);

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Yükleniyor...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modern-dashboard corporate-dashboard">
      <div className="row mb-4">
        <div className="col-12">
          <div className="corporate-hero-card">
            <div>
              <span className="corporate-hero-badge">Kurumsal anasayfa</span>
              <h1>Bilgiye, duyurulara ve temel kaynaklara tek yerden erisin</h1>
              <p>
                Güncel kurumsal iletişim, yönetim mesajları, kültür içerikleri ve sık kullanılan
                dokuman akışları bu sayfada bir araya getirildi.
              </p>
            </div>
            <div className="corporate-hero-actions">
              <button className="btn btn-modern-primary" onClick={() => navigate(ROUTE_PATHS.announcements)}>
                Duyuruları aç
              </button>
              <button className="btn btn-modern-secondary" onClick={() => navigate(ROUTE_PATHS.documents)}>
                Dokuman merkezine git
              </button>
              <button
                className="btn btn-modern-secondary"
                onClick={() => openLink(ORGANIZATION_BRAND.academyUrl, true)}
              >
                Koloğlu Akademi
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-12">
          <div className="modern-dashboard-header">
            <div className="modern-stat-card">
              <div className="modern-stat-icon">
                <i className="bx bxs-megaphone"></i>
              </div>
              <div className="modern-stat-content">
                <h3 className="modern-stat-number">{dashboardData.statistics.totalAnnouncements}</h3>
                <p className="modern-stat-label">Duyuru</p>
              </div>
            </div>

            <div className="modern-stat-card">
              <div className="modern-stat-icon">
                <i className="bx bxs-file-doc"></i>
              </div>
              <div className="modern-stat-content">
                <h3 className="modern-stat-number">{dashboardData.statistics.totalDocuments}</h3>
                <p className="modern-stat-label">Dokuman</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-lg-6 mb-4 mb-lg-0">
          <div className="modern-widget">
            <div className="modern-widget-header">
              <div className="modern-widget-title">
                <i className="bx bxs-message-detail"></i>
                <h4>Yönetimden Mesajlar</h4>
              </div>
            </div>
            <div className="modern-widget-body">
              <div className="corporate-message-list">
                {managementMessages.map((message) => (
                  <div key={message.id} className="corporate-message-card">
                    <span className="corporate-pill">{message.badge}</span>
                    <h5>{message.title}</h5>
                    <p>{message.summary}</p>
                    <div className="corporate-message-footer">
                      <div>
                        <strong>{message.author}</strong>
                        <small>{message.role}</small>
                      </div>
                      <button
                        className="btn btn-link text-decoration-none px-0"
                        onClick={() => navigate(message.ctaPath)}
                      >
                        {message.ctaLabel}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-6">
          <div className="modern-widget">
            <div className="modern-widget-header">
              <div className="modern-widget-title">
                <i className="bx bxs-flag-alt"></i>
                <h4>Biz Koloğluyuz</h4>
              </div>
            </div>
            <div className="modern-widget-body">
              <div className="row g-3">
                {cultureHighlights.map((item) => (
                  <div key={item.id} className="col-md-6">
                    <div className="corporate-culture-card">
                      <div className="corporate-culture-icon">
                        <i className={item.icon}></i>
                      </div>
                      <h6>{item.title}</h6>
                      <p>{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-6 col-md-12 mb-4">
          <div className="modern-widget">
            <div className="modern-widget-header">
              <div className="modern-widget-title">
                <i className="bx bxs-conversation"></i>
                <h4>Katılım Alanları</h4>
              </div>
            </div>
            <div className="modern-widget-body">
              <div className="row g-3">
                {participationLinks.map((item) => (
                  <div key={item.id} className="col-md-12">
                    <button
                      className="modern-quick-link corporate-system-link text-start w-100"
                      onClick={() => navigate(item.path)}
                    >
                      <i className={item.icon}></i>
                      <span>{item.title}</span>
                      <small>{item.description}</small>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-6 col-md-12 mb-4">
          <div className="modern-widget">
            <div className="modern-widget-header">
              <div className="modern-widget-title">
                <i className="bx bxs-megaphone"></i>
                <h4>Merkezi Duyurular</h4>
              </div>
              <button className="modern-widget-action" onClick={() => navigate(ROUTE_PATHS.announcements)}>
                <i className="bx bx-right-arrow-alt"></i>
              </button>
            </div>
            <div className="modern-widget-body">
              {dashboardData.announcements.length > 0 ? (
                <div className="modern-activity-list">
                  {dashboardData.announcements.slice(0, 4).map((announcement, index) => (
                    <div key={index} className="modern-activity-item">
                      <div className="modern-activity-icon">
                        <i className="bx bxs-megaphone"></i>
                      </div>
                      <div className="modern-activity-content">
                        <h6 className="modern-activity-title">{announcement.title}</h6>
                        <p className="modern-activity-meta">
                          {new Date(announcement.createdAt).toLocaleDateString('tr-TR', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}{' '}
                          - {announcement.author?.firstName} {announcement.author?.lastName}
                        </p>
                        {announcement.content && (
                          <p className="modern-activity-description">
                            {announcement.content.length > 120
                              ? `${announcement.content.substring(0, 120)}...`
                              : announcement.content}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="modern-empty-state">
                  <i className="bx bxs-megaphone"></i>
                  <p>Henuz duyuru bulunmuyor</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-6 col-md-12 mb-4">
          <div className="modern-widget">
            <div className="modern-widget-header">
              <div className="modern-widget-title">
                <i className="bx bxs-building-house"></i>
                <h4>Kurumsal Kaynaklar</h4>
              </div>
            </div>
            <div className="modern-widget-body">
              <div className="row g-3">
                {corporateQuickAccess.map((item) => (
                  <div key={item.id} className="col-md-6">
                    <button
                      className="modern-quick-link corporate-resource-link h-100 w-100 text-start"
                      onClick={() => navigate(item.path)}
                    >
                      <i className={item.icon}></i>
                      <span>{item.title}</span>
                      <small className="d-block mt-1 text-muted">{item.description}</small>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-6 col-md-12 mb-4">
          <div className="modern-widget">
            <div className="modern-widget-header">
              <div className="modern-widget-title">
                <i className="bx bx-support"></i>
                <h4>Operasyonel Self-Service</h4>
              </div>
              <button className="modern-widget-action" onClick={() => navigate(ROUTE_PATHS.selfService)}>
                <i className="bx bx-right-arrow-alt"></i>
              </button>
            </div>
            <div className="modern-widget-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="corporate-culture-card h-100">
                    <div className="corporate-culture-icon">
                      <i className="bx bxs-dish"></i>
                    </div>
                    <h6>Bugunun Menusu</h6>
                    <p>
                      {currentLunchMenu
                        ? `${currentLunchMenu.main} ve ${currentLunchMenu.side}`
                        : 'Menu bilgisi hazirlaniyor.'}
                    </p>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="corporate-culture-card h-100">
                    <div className="corporate-culture-icon">
                      <i className="bx bxs-help-circle"></i>
                    </div>
                    <h6>Teknik SSS</h6>
                    <p>{techFaqs[0]?.question || 'Destek icerigi hazirlaniyor.'}</p>
                  </div>
                </div>
              </div>

              <div className="corporate-contact-grid mt-3">
                {operationsContacts.slice(0, 2).map((contact) => (
                  <div key={contact.id} className="corporate-contact-card">
                    <div className="corporate-contact-icon">
                      <i className={contact.icon}></i>
                    </div>
                    <div>
                      <h6>{contact.title}</h6>
                      <p>{contact.detail}</p>
                      <small>{contact.contact}</small>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-6 col-md-12 mb-4">
          <div className="modern-widget">
            <div className="modern-widget-header">
              <div className="modern-widget-title">
                <i className="bx bxs-gift"></i>
                <h4>Son Bir Ay Icindeki Dogum Gunleri</h4>
              </div>
            </div>
            <div className="modern-widget-body">
              {dashboardData.birthdays.length > 0 ? (
                <div className="modern-birthday-list">
                  {dashboardData.birthdays.map((person, index) => {
                    const birthDate = new Date(person.birthDate);
                    const displayDate = new Date(
                      new Date().getFullYear(),
                      birthDate.getMonth(),
                      birthDate.getDate()
                    );

                    return (
                      <div key={index} className="modern-birthday-item">
                        <div className="modern-birthday-avatar">
                          <i className="bx bxs-gift"></i>
                        </div>
                        <div className="modern-birthday-content">
                          <h6 className="modern-birthday-name">
                            {person.firstName} {person.lastName}
                          </h6>
                          <p className="modern-birthday-department">{person.department}</p>
                          <small className="modern-birthday-date">
                            {displayDate.toLocaleDateString('tr-TR', {
                              day: 'numeric',
                              month: 'long'
                            })}
                          </small>
                        </div>
                        <div className="modern-birthday-badge">
                          <i className="bx bxs-cake"></i>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="modern-empty-state">
                  <i className="bx bxs-gift"></i>
                  <p>Son bir ay icinde dogum gunu kaydi bulunmuyor</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-6 col-md-12 mb-4">
          <div className="modern-widget">
            <div className="modern-widget-header">
              <div className="modern-widget-title">
                <i className="bx bxs-zap"></i>
                <h4>Hizli Iletisim ve Sistemler</h4>
              </div>
            </div>
            <div className="modern-widget-body">
              <div className="corporate-contact-grid">
                {quickContacts.map((contact) => (
                  <div key={contact.id} className="corporate-contact-card">
                    <div className="corporate-contact-icon">
                      <i className={contact.icon}></i>
                    </div>
                    <div>
                      <h6>{contact.title}</h6>
                      <p>{contact.detail}</p>
                      <small>{contact.contact}</small>
                    </div>
                  </div>
                ))}
              </div>

              <div className="corporate-system-links">
                {systemLinks.map((link) => (
                  <button
                    key={link.id}
                    className="modern-quick-link corporate-system-link"
                    onClick={() => openLink(link.path, link.external)}
                  >
                    <i className={link.icon}></i>
                    <span>{link.title}</span>
                    <small>{link.description}</small>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
